// ============================================================
// Ad-platform destinations — shared types, consent gate, config validation
//
// Server-side forwarding to Meta Conversions API and GA4 Measurement Protocol,
// as destination TYPES next to the signed webhook. Consent gating is the
// product: an event is forwarded only with explicit marketing consent and no
// GPC signal; everything else is logged `suppressed_consent`.
// ============================================================
import { EVENT_NAME_RE } from '../config.js';
import { advertisingAllowed } from '../consent.js';
export const WEBHOOK_TYPE = 'webhook';
export const AD_DESTINATION_TYPES = ['meta_capi', 'ga4_mp'];
/** Designed, schema only — not creatable yet (docs/destinations/google-ads-ec.md). */
export const PLANNED_DESTINATION_TYPES = ['google_ads_ec'];
export function isAdDestinationType(t) {
    return typeof t === 'string' && AD_DESTINATION_TYPES.includes(t);
}
export const MAX_EVENT_MAP = 50;
/** Platform responses kept in the delivery log. */
export const AD_RESPONSE_SNIPPET_CHARS = 500;
export const AD_DELIVERY_TIMEOUT_MS = 10_000;
/**
 * Advertising consent is explicit (envelope `purposes.advertising` / legacy `marketing: true`);
 * GPC always wins. No consent → nothing leaves YOM for an ad platform. The decision itself is
 * consent.ts `advertisingAllowed` (one gate for forwarders and audience code).
 */
export function adConsentGate(ev) {
    return advertisingAllowed(ev) ? 'forward' : 'suppressed_consent';
}
/** Strip a credential from any text we may log (responses, error messages). */
export function redactSecret(text, ...secrets) {
    let out = text;
    for (const s of secrets) {
        if (s && s.length >= 6) {
            out = out.split(s).join('[redacted]');
        }
    }
    // Belt and braces: query-string credentials in echoed URLs.
    return out.replace(/(access_token|api_secret)=[^&\s"']+/g, '$1=[redacted]');
}
export function snippet(text, ...secrets) {
    return redactSecret(text, ...secrets).slice(0, AD_RESPONSE_SNIPPET_CHARS);
}
/** Masked credential for GET responses: last 4 characters only. */
export function maskCredential(secret) {
    return secret.length <= 8 ? '••••' : `••••${secret.slice(-4)}`;
}
export function pageUrl(ev) {
    // Path-only policy: host + path, never a query string or fragment.
    return ev.host ? `https://${ev.host}${ev.path || '/'}` : '';
}
export async function postJson(url, body, fetchImpl, secrets) {
    const started = Date.now();
    try {
        const res = await fetchImpl(url, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
            redirect: 'manual',
            signal: AbortSignal.timeout(AD_DELIVERY_TIMEOUT_MS),
        });
        const text = await res.text().catch(() => '');
        return { status: res.status, text, durationMs: Date.now() - started, error: '' };
    }
    catch (err) {
        return {
            status: 0,
            text: '',
            durationMs: Date.now() - started,
            error: redactSecret(err instanceof Error ? `${err.name}: ${err.message}` : 'network error', ...secrets).slice(0, 300),
        };
    }
}
// --- Config validation (API create/update) ---
export class AdConfigError extends Error {
}
const META_PIXEL_RE = /^\d{5,20}$/;
const META_TOKEN_RE = /^[A-Za-z0-9_|-]{20,1024}$/;
const META_TEST_CODE_RE = /^[A-Za-z0-9_]{1,64}$/;
const META_EVENT_RE = /^[A-Za-z][A-Za-z0-9_]{0,49}$/;
export const GA4_MEASUREMENT_RE = /^G-[A-Z0-9]{4,20}$/;
const GA4_SECRET_RE = /^[A-Za-z0-9_-]{8,128}$/;
const GA4_EVENT_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;
const GA4_RESERVED_EVENTS = new Set([
    'ad_activeview',
    'ad_click',
    'ad_exposure',
    'ad_query',
    'ad_reward',
    'adunit_exposure',
    'app_clear_data',
    'app_exception',
    'app_install',
    'app_remove',
    'app_store_refund',
    'app_store_subscription_cancel',
    'app_store_subscription_convert',
    'app_store_subscription_renew',
    'app_update',
    'app_upgrade',
    'dynamic_link_app_open',
    'dynamic_link_app_update',
    'dynamic_link_first_open',
    'error',
    'first_open',
    'first_visit',
    'in_app_purchase',
    'notification_dismiss',
    'notification_foreground',
    'notification_open',
    'notification_receive',
    'os_update',
    'session_start',
    'session_start_with_rollout',
    'user_engagement',
]);
function parseEventMap(v, valueRe, label) {
    if (v === undefined || v === null) {
        return {};
    }
    if (typeof v !== 'object' || Array.isArray(v)) {
        throw new AdConfigError('event_map must be an object { "<yom_event>": "<platform event>" }');
    }
    const entries = Object.entries(v);
    if (entries.length > MAX_EVENT_MAP) {
        throw new AdConfigError(`event_map allows at most ${MAX_EVENT_MAP} entries`);
    }
    const out = {};
    for (const [k, val] of entries) {
        if (!EVENT_NAME_RE.test(k)) {
            throw new AdConfigError(`event_map key "${k}" is not a YOM event name`);
        }
        if (typeof val !== 'string' || !valueRe.test(val)) {
            throw new AdConfigError(`event_map["${k}"] must be a valid ${label} event name`);
        }
        if (label === 'GA4' &&
            (GA4_RESERVED_EVENTS.has(val) || /^(ga_|google_|firebase_)/.test(val))) {
            throw new AdConfigError(`event_map["${k}"]: "${val}" is reserved by GA4`);
        }
        out[k] = val;
    }
    return out;
}
function parseEndpoint(v, allowInsecure) {
    if (v === undefined || v === null || v === '') {
        return undefined;
    }
    if (!allowInsecure) {
        throw new AdConfigError('endpoint overrides are only available in local development');
    }
    if (typeof v !== 'string' || !/^https?:\/\/[^\s/]+(\/[^\s]*)?$/.test(v)) {
        throw new AdConfigError('endpoint must be an http(s) base URL');
    }
    return v.replace(/\/$/, '');
}
/**
 * Validate the type-specific part of a create/update body. `existing` is the stored config
 * for a partial update (credential optional then).
 */
export function parseAdDestinationInput(type, b, existing, allowInsecure = false) {
    const partial = existing !== null;
    if (type === 'meta_capi') {
        const prev = existing;
        const pixel = b.pixel_id === undefined && prev ? prev.pixel_id : b.pixel_id;
        if (typeof pixel !== 'string' || !META_PIXEL_RE.test(pixel)) {
            throw new AdConfigError('pixel_id must be the numeric Meta pixel / dataset id');
        }
        let credential;
        if (!partial || b.access_token !== undefined) {
            if (typeof b.access_token !== 'string' || !META_TOKEN_RE.test(b.access_token)) {
                throw new AdConfigError('access_token must be a Conversions API access token');
            }
            credential = b.access_token;
        }
        let testCode = prev?.test_event_code;
        if (b.test_event_code !== undefined) {
            if (b.test_event_code === null || b.test_event_code === '') {
                testCode = undefined;
            }
            else if (typeof b.test_event_code !== 'string' ||
                !META_TEST_CODE_RE.test(b.test_event_code)) {
                throw new AdConfigError('test_event_code must be the code from Events Manager › Test events');
            }
            else {
                testCode = b.test_event_code;
            }
        }
        const eventMap = b.event_map === undefined
            ? (prev?.event_map ?? {})
            : parseEventMap(b.event_map, META_EVENT_RE, 'Meta');
        const endpoint = b.endpoint === undefined ? prev?.endpoint : parseEndpoint(b.endpoint, allowInsecure);
        return {
            config: {
                pixel_id: pixel,
                ...(testCode ? { test_event_code: testCode } : {}),
                ...(Object.keys(eventMap).length ? { event_map: eventMap } : {}),
                ...(endpoint ? { endpoint } : {}),
            },
            credential,
        };
    }
    const prev = existing;
    const mid = b.measurement_id === undefined && prev ? prev.measurement_id : b.measurement_id;
    if (typeof mid !== 'string' || !GA4_MEASUREMENT_RE.test(mid)) {
        throw new AdConfigError('measurement_id must look like G-XXXXXXX');
    }
    let credential;
    if (!partial || b.api_secret !== undefined) {
        if (typeof b.api_secret !== 'string' || !GA4_SECRET_RE.test(b.api_secret)) {
            throw new AdConfigError('api_secret must be a Measurement Protocol API secret');
        }
        credential = b.api_secret;
    }
    const eventMap = b.event_map === undefined
        ? (prev?.event_map ?? {})
        : parseEventMap(b.event_map, GA4_EVENT_RE, 'GA4');
    const endpoint = b.endpoint === undefined ? prev?.endpoint : parseEndpoint(b.endpoint, allowInsecure);
    return {
        config: {
            measurement_id: mid,
            ...(Object.keys(eventMap).length ? { event_map: eventMap } : {}),
            ...(endpoint ? { endpoint } : {}),
        },
        credential,
    };
}
export function parseStoredAdConfig(raw) {
    try {
        const v = JSON.parse(raw ?? '{}');
        return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
    }
    catch {
        return null;
    }
}
/** Non-secret config for GET responses (the local endpoint override is shown as-is). */
export function publicAdConfig(type, config) {
    const out = { ...config };
    out.event_map = effectiveEventMap(type, config);
    return out;
}
// Default maps live here (not in meta.ts / ga4.ts) so routing can use them without the senders.
export const META_DEFAULT_EVENT_MAP = {
    purchase: 'Purchase',
    generate_lead: 'Lead',
    sign_up: 'CompleteRegistration',
    add_to_cart: 'AddToCart',
    begin_checkout: 'InitiateCheckout',
    view_item: 'ViewContent',
};
/** GA4 recommended events already share YOM's names. */
export const GA4_DEFAULT_EVENT_MAP = {
    purchase: 'purchase',
    generate_lead: 'generate_lead',
    sign_up: 'sign_up',
    add_to_cart: 'add_to_cart',
    begin_checkout: 'begin_checkout',
    view_item: 'view_item',
};
export function effectiveEventMap(type, config) {
    const base = type === 'meta_capi' ? META_DEFAULT_EVENT_MAP : GA4_DEFAULT_EVENT_MAP;
    return { ...base, ...(config.event_map ?? {}) };
}
/** Ad-platform destinations route exactly their mapped events (keeps queue traffic bounded). */
export function adEventFilters(type, config) {
    return Object.keys(effectiveEventMap(type, config)).map(e => `event.${e}`);
}
//# sourceMappingURL=common.js.map